import { useSelector } from "react-redux"

import { BP_PATH, PATH } from "containers/Survey/models"
import {
  getCurrentQuestion,
  getDoctorTypeAnswer,
  getSurveyPath,
} from "containers/Survey/services/selector"
import { DoctorType } from "types/entity"

interface StepGroupDef {
  members: PATH[]
}

interface StepGroup {
  step: number
  members: PATH[]
}

export interface StepperResult {
  current: number
  insideStep: number
  totalInsideSteps: number
  totalSteps: number
}

const stepGroupsGeneralist: StepGroupDef[] = [
  { members: [PATH.doctor_type] },
  { members: [PATH.patient, PATH.exemption] },
  { members: [PATH.inform, PATH.reason, PATH.vitals] },
  {
    members: [
      PATH.anamnesis_questions,
      PATH.pregnant,
      PATH.allergy,
      PATH.allergy_details,
      PATH.illness,
      PATH.illness_details,
    ],
  },
  {
    members: [PATH.upload_documents, PATH.selected_documents],
  },
  { members: [PATH.ineligible] },
  { members: [PATH.doctor_available] },
  { members: [PATH.diagnostic_devices] },
]

const isPathMember = (pathId: string, memberId: string): boolean =>
  pathId === memberId || pathId.startsWith(`${memberId}:`)

const stepGroupsDermatologist: StepGroupDef[] = [
  { members: [PATH.dermato_overview_part1, PATH.dermato_overview_info, PATH.dermato_overview_part2, PATH.pharmacist_must_be_here] },
  { members: [PATH.doctor_type, PATH.patient, PATH.exemption] },
  { members: [PATH.inform, PATH.reason] },
  {
    members: [
      PATH.anamnesis_questions,
      PATH.pregnant,
      PATH.allergy,
      PATH.illness,
      PATH.dermato_anamnesis_questions,
      PATH.dermato_questions,
      PATH.dermato_last_control,
      PATH.dermato_sun_exposure,
      PATH.dermato_sun_protection,
      PATH.dermato_sunburn_before_20,
      PATH.dermato_history,
      PATH.dermato_family_cancer,
      PATH.dermato_recent_travel,
      PATH.dermato_zone_evolution,
      PATH.dermato_problem_duration,
      PATH.dermato_zone_evolution_duration,
      PATH.dermato_body_zone,
      PATH.dermato_phototype,
      PATH.dermato_symptoms,
    ],
  },
  { members: [PATH.upload_documents, PATH.selected_documents] },
  { members: [PATH.dermato_video_capture] },
  { members: [PATH.dermato_recap] },
  { members: [PATH.dermato_consent] },
]

const stepGroupsBP: StepGroupDef[] = [
  { members: [PATH.doctor_type] },
  { members: [BP_PATH.prevention_handoff as any, BP_PATH.prevention_report_info as any] },
  { members: [PATH.patient, PATH.exemption] },
]
const stepGroupsCheckup: StepGroupDef[] = [
  { members: [PATH.doctor_type] },
  { members: [PATH.patient, PATH.exemption] },
  { members: [PATH.checkup_overview] },
  { members: [PATH.checkup_vitals] },
  { members: [PATH.checkup_report] },
]

function getStepGroups(type: DoctorType | undefined | null): StepGroupDef[] {
  switch (type) {
    case DoctorType.DERMATOLOGY:
      return stepGroupsDermatologist
    case DoctorType.PHARMACIST:
      return stepGroupsBP;
    case DoctorType.CHECKUP:
      return stepGroupsCheckup
    case DoctorType.GENERALIST:
    default:
      return stepGroupsGeneralist
  }
}

/**
 * filtre les différents groupes d'étapes en fonction du path
 * Supprime les étapes vides
 * Rénumérote les étapes restantes à partir de 1
 */
function filterStepMembersFromPath(
  path: string[],
  stepGroups: StepGroupDef[],
): StepGroup[] {
  const filtered = stepGroups
    .map((group) =>
      group.members.filter((memberId) =>
        path.some((p) => isPathMember(p, memberId)),
      ),
    )
    .filter((members) => members.length > 0)
    .map((members, index) => ({ step: index + 1, members }))

  return filtered
}

export function useStepper(params?: {
  current?: number
  insideStep?: number
  totalInsideSteps?: number
  totalSteps?: number
}): StepperResult {
  const surveyPath = useSelector(getSurveyPath)
  const doctorType = useSelector(getDoctorTypeAnswer)
  const currentQuestion = useSelector(getCurrentQuestion)

  let stepGroups = getStepGroups(doctorType as DoctorType)

  if(doctorType === DoctorType.PHARMACIST) {
    // @TODO handle exemption path
    // @TODO : do not show if exemption question is not passed by yet
    // Group by contiguous section runs of the walked path: the questions are
    // reordered (theme selector, mandatory, themes), so grouping by template
    // section number would start the stepper at its last step.
    const runs: string[][] = []
    let lastSection: string | undefined
    for (const p of surveyPath || []) {
      const section = p.match(/^prevention-report-([0-9]+)_[0-9]+/)?.[1]
      if (!section) continue
      if (section !== lastSection) runs.push([])
      runs[runs.length - 1].push(p)
      lastSection = section
    }
    stepGroups = runs.map((members) => ({ members })) as any
  }
  if (params?.current !== undefined) {
    return {
      current: params.current,
      insideStep: params.insideStep ?? 1,
      totalInsideSteps: params.totalInsideSteps ?? 1,
      totalSteps: params.totalSteps ?? 6,
    }
  }

  const questionId = currentQuestion?.id
  const path = surveyPath || []
  const filteredStepGroups = filterStepMembersFromPath(path, stepGroups)
  const TOTAL_STEPS = filteredStepGroups.length

  if (!questionId || !path.length) {
    return {
      current: 0,
      insideStep: 0,
      totalInsideSteps: 0,
      totalSteps: TOTAL_STEPS,
    }
  }

  const group = filteredStepGroups.find((g) =>
    g.members.some((memberId) => isPathMember(questionId, memberId)),
  )

  if (!group) {
    return {
      current: 0,
      insideStep: 0,
      totalInsideSteps: 0,
      totalSteps: TOTAL_STEPS,
    }
  }

  const groupPath = path.filter((p) =>
    group.members.some((memberId) => isPathMember(p, memberId)),
  )

  const insideIndex = groupPath.findIndex((p) => p === questionId)
  const insideStep = insideIndex === -1 ? 1 : insideIndex + 1
  const totalInsideSteps = groupPath.length || 1

  return {
    current: group.step,
    insideStep,
    totalInsideSteps,
    totalSteps: TOTAL_STEPS,
  }
}
